import {
  createClient,
  type AuthChangeEvent,
  type Session,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { inspectAuthReturnUrl } from "./auth-return-result.js";

export interface PublicAuthConfig {
  readonly supabaseUrl: string;
  readonly publishableKey: string;
  readonly googleEnabled: boolean;
}

export const PREVENTIVONOW_SUPABASE_PROJECT_REF = "nermnztjxpzuoozsejgy";

export interface AuthIdentity {
  readonly userId: string;
  readonly email: string | null;
  readonly accessToken: string;
}

export interface AuthGateway {
  getIdentity(): Promise<AuthIdentity | null>;
  getAccessToken(): Promise<string>;
  onIdentityChange(
    listener: (identity: AuthIdentity | null, event: AuthChangeEvent) => void,
  ): () => void;
  sendEmailLink(email: string, redirectTo: string): Promise<void>;
  requestPasswordReset(email: string, redirectTo: string): Promise<void>;
  signInWithGoogle(redirectTo: string): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  signUpWithPassword(email: string, password: string, redirectTo: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
  signOut(): Promise<void>;
}

export interface AuthController {
  getIdentity(): AuthIdentity | null;
  getAccessToken(): Promise<string>;
  subscribe(listener: (identity: AuthIdentity | null) => void): () => void;
  dispose(): void;
}

export const UNEXPECTED_SIGN_OUT_MESSAGE =
  "La sessione è terminata o è stata chiusa da un’altra scheda. Il preventivo locale è rimasto aperto: accedi di nuovo per ricollegare l’archivio cifrato.";

export function shouldNotifyUnexpectedSignOut(input: {
  readonly event: AuthChangeEvent;
  readonly hadIdentity: boolean;
  readonly hasNextIdentity: boolean;
  readonly suppressed: boolean;
}): boolean {
  return input.event === "SIGNED_OUT"
    && input.hadIdentity
    && !input.hasNextIdentity
    && !input.suppressed;
}

interface AuthMountOptions {
  readonly trigger: HTMLButtonElement;
  readonly dialog: HTMLDialogElement;
  readonly vaultBridge: HTMLElement;
  readonly config: PublicAuthConfig | null;
  readonly gateway?: AuthGateway;
  readonly beforeExternalAuthRedirect?: () => void;
}

function decodeLegacyJwtPayload(token: string): Record<string, unknown> | null {
  const part = token.split(".")[1];
  if (part === undefined) return null;
  try {
    const padded = part.replaceAll("-", "+").replaceAll("_", "/")
      + "=".repeat((4 - (part.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function normalizeSupabasePublicUrl(value: string): string {
  const url = new URL(value);
  const loopback = ["127.0.0.1", "localhost", "::1"].includes(url.hostname);
  if (url.protocol !== "https:" && !(loopback && url.protocol === "http:")) {
    throw new Error("Supabase richiede HTTPS; HTTP è ammesso soltanto in locale");
  }
  if (url.username || url.password || url.search || url.hash) {
    throw new Error("URL Supabase pubblico non valido");
  }
  if (!loopback) {
    const expectedHostname = `${PREVENTIVONOW_SUPABASE_PROJECT_REF}.supabase.co`;
    if (url.hostname !== expectedHostname) {
      throw new Error("Progetto Supabase non autorizzato per PreventivoNow");
    }
  }
  return url.origin;
}

export function assertBrowserSafeSupabaseKey(value: string): string {
  const key = value.trim();
  if (key === "") throw new Error("Chiave pubblicabile Supabase mancante");
  if (key.startsWith("sb_secret_")) {
    throw new Error("Una secret key Supabase non deve mai entrare nel browser");
  }
  if (key.startsWith("sb_publishable_")) return key;
  const payload = decodeLegacyJwtPayload(key);
  if (payload?.role !== "anon") {
    throw new Error("Il browser accetta soltanto publishable key o legacy anon key");
  }
  return key;
}

export function readPublicAuthConfig(
  environment: Record<string, string | boolean | undefined>,
): PublicAuthConfig | null {
  const rawUrl = environment.VITE_SUPABASE_URL;
  const rawKey = environment.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (typeof rawUrl !== "string" && typeof rawKey !== "string") return null;
  if (typeof rawUrl !== "string" || typeof rawKey !== "string") {
    throw new Error("Configurazione Auth incompleta: URL e publishable key devono essere presenti insieme");
  }
  return {
    supabaseUrl: normalizeSupabasePublicUrl(rawUrl),
    publishableKey: assertBrowserSafeSupabaseKey(rawKey),
    googleEnabled: environment.VITE_GOOGLE_AUTH_ENABLED === "true"
      || environment.VITE_GOOGLE_AUTH_ENABLED === true,
  };
}

export function authReturnUrl(location: Pick<Location, "origin">): string {
  return new URL("/equocompensonow/", location.origin).toString();
}

function mapSession(session: Session | null): AuthIdentity | null {
  if (session === null) return null;
  return {
    userId: session.user.id,
    email: session.user.email ?? null,
    accessToken: session.access_token,
  };
}

export class SupabaseBrowserAuthGateway implements AuthGateway {
  readonly #client: SupabaseClient;

  constructor(client: SupabaseClient) {
    this.#client = client;
  }

  async getIdentity(): Promise<AuthIdentity | null> {
    const { data, error } = await this.#client.auth.getSession();
    if (error !== null) throw error;
    return mapSession(data.session);
  }

  async getAccessToken(): Promise<string> {
    const identity = await this.getIdentity();
    if (identity === null) throw new Error("Sessione Supabase assente");
    return identity.accessToken;
  }

  onIdentityChange(
    listener: (identity: AuthIdentity | null, event: AuthChangeEvent) => void,
  ): () => void {
    const { data } = this.#client.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => listener(mapSession(session), event),
    );
    return () => data.subscription.unsubscribe();
  }

  async sendEmailLink(email: string, redirectTo: string): Promise<void> {
    const { error } = await this.#client.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo, shouldCreateUser: true },
    });
    if (error !== null) throw error;
  }

  async requestPasswordReset(email: string, redirectTo: string): Promise<void> {
    const { error } = await this.#client.auth.resetPasswordForEmail(email, { redirectTo });
    if (error !== null) throw error;
  }

  async signInWithGoogle(redirectTo: string): Promise<void> {
    const { error } = await this.#client.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error !== null) throw error;
  }

  async signInWithPassword(email: string, password: string): Promise<void> {
    const { error } = await this.#client.auth.signInWithPassword({ email, password });
    if (error !== null) throw error;
  }

  async signUpWithPassword(
    email: string,
    password: string,
    redirectTo: string,
  ): Promise<void> {
    const { error } = await this.#client.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo },
    });
    if (error !== null) throw error;
  }

  async updatePassword(password: string): Promise<void> {
    const { error } = await this.#client.auth.updateUser({ password });
    if (error !== null) throw error;
  }

  async signOut(): Promise<void> {
    const { error } = await this.#client.auth.signOut({ scope: "local" });
    if (error !== null) throw error;
  }
}

export function createBrowserAuthGateway(config: PublicAuthConfig): AuthGateway {
  const projectIdentity = new URL(config.supabaseUrl).hostname.replaceAll(".", "-");
  const client = createClient(config.supabaseUrl, config.publishableKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      persistSession: true,
      storageKey: `preventivonow-auth-${projectIdentity}`,
    },
  });
  return new SupabaseBrowserAuthGateway(client);
}

function initials(identity: AuthIdentity): string {
  const source = identity.email?.split("@")[0] ?? "utente";
  const parts = source.split(/[._\-\s]+/u).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "UT";
}

function friendlyAuthError(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("invalid login credentials")) return "Email o password non corrette.";
  if (message.includes("password")) return "La password non rispetta i requisiti oppure non è corretta.";
  if (message.includes("rate") || message.includes("seconds")) {
    return "Troppe richieste ravvicinate. Attendi qualche istante e riprova.";
  }
  if (message.includes("provider") || message.includes("google")) {
    return "L’accesso Google non è ancora configurato per questo ambiente.";
  }
  return "Accesso non riuscito. Riprova senza modificare i dati del preventivo.";
}

export function validateReplacementPassword(
  password: string,
  confirmation: string,
): string | null {
  if (password.length < 10) return "La nuova password deve contenere almeno 10 caratteri.";
  if (password.length > 256) return "La nuova password è troppo lunga.";
  if (password !== confirmation) return "Le due password non coincidono.";
  return null;
}

function setBusy(dialog: HTMLDialogElement, busy: boolean): void {
  dialog.querySelectorAll<HTMLButtonElement>(
    "button[type='submit'], [data-auth-google], [data-auth-reset], [data-auth-signout], [data-auth-recovery-cancel]",
  )
    .forEach((button) => {
      button.disabled = busy || button.dataset.authProviderDisabled === "true";
    });
  dialog.setAttribute("aria-busy", String(busy));
}

function showAuthPanel(
  dialog: HTMLDialogElement,
  panel: "signed-out" | "signed-in" | "recovery",
): void {
  const panels = {
    "signed-out": dialog.querySelector<HTMLElement>("[data-auth-signed-out]"),
    "signed-in": dialog.querySelector<HTMLElement>("[data-auth-signed-in]"),
    recovery: dialog.querySelector<HTMLElement>("[data-auth-recovery]"),
  };
  Object.entries(panels).forEach(([name, element]) => {
    if (element !== null) element.hidden = name !== panel;
  });
  const heading = dialog.querySelector<HTMLElement>("#auth-dialog-title");
  if (heading !== null) {
    heading.textContent = panel === "recovery" ? "Scegli una nuova password" : "Accedi senza perdere tempo";
  }
}

function setOutcome(dialog: HTMLDialogElement, message: string, tone: "ok" | "error"): void {
  const outcome = dialog.querySelector<HTMLElement>("[data-auth-outcome]");
  if (outcome === null) return;
  outcome.textContent = message;
  outcome.dataset.tone = tone;
  outcome.hidden = false;
}

function updateIdentityUi(
  trigger: HTMLButtonElement,
  vaultBridge: HTMLElement,
  identity: AuthIdentity | null,
): void {
  trigger.classList.toggle("is-authenticated", identity !== null);
  trigger.textContent = identity === null ? "Accedi" : initials(identity);
  trigger.setAttribute(
    "aria-label",
    identity === null ? "Accedi all’area privata" : `Account ${identity.email ?? "utente"}`,
  );
  vaultBridge.dataset.authenticated = String(identity !== null);
  const title = document.createElement("strong");
  const description = document.createElement("span");
  title.textContent = identity === null ? "Archivio online non collegato" : "Sessione attiva";
  description.textContent = identity === null
    ? "Accedi soltanto se vuoi salvare e ritrovare online i preventivi. Il calcolo e il download del PDF non richiedono un account."
    : `Account ${identity.email ?? "senza email"}. Il cloud riceve soltanto gli artefatti già cifrati dal browser.`;
  vaultBridge.replaceChildren(title, description);
}

export async function mountAuthControls(options: AuthMountOptions): Promise<AuthController> {
  const { trigger, dialog, vaultBridge, config } = options;
  const googleButton = dialog.querySelector<HTMLButtonElement>("[data-auth-google]");
  if (googleButton !== null) {
    const googleEnabled = config?.googleEnabled === true;
    googleButton.dataset.authProviderDisabled = String(!googleEnabled);
    googleButton.disabled = !googleEnabled;
    googleButton.title = googleEnabled
      ? "Accedi con Google"
      : "Accesso Google in attivazione; usa il link email o la password";
    const label = googleButton.querySelector<HTMLElement>("strong");
    if (label !== null && !googleEnabled) label.textContent = "Google in attivazione";
  }
  const authReturn = inspectAuthReturnUrl(window.location.href);
  if (config === null) {
    if (authReturn.detected) {
      window.history.replaceState(window.history.state, "", authReturn.cleanRelativeUrl);
    }
    trigger.textContent = "Accedi";
    updateIdentityUi(trigger, vaultBridge, null);
    trigger.addEventListener("click", () => dialog.showModal());
    dialog.querySelector<HTMLElement>("[data-auth-unconfigured]")?.removeAttribute("hidden");
    dialog.querySelector<HTMLElement>("[data-auth-signed-out]")?.setAttribute("hidden", "");
    dialog.querySelector<HTMLElement>("[data-auth-signed-in]")?.setAttribute("hidden", "");
    dialog.querySelector<HTMLElement>("[data-auth-recovery]")?.setAttribute("hidden", "");
    return {
      getIdentity: () => null,
      getAccessToken: async () => {
        throw new Error("Sessione Supabase non configurata");
      },
      subscribe(listener) {
        listener(null);
        return () => undefined;
      },
      dispose: () => undefined,
    };
  }

  const gateway = options.gateway ?? createBrowserAuthGateway(config);
  let identity: AuthIdentity | null = null;
  let recoveringPassword = false;
  let suppressSignedOutNotice = false;
  const listeners = new Set<(identity: AuthIdentity | null) => void>();

  const applyIdentity = (nextIdentity: AuthIdentity | null): void => {
    const previousUserId = identity?.userId ?? null;
    identity = nextIdentity;
    updateIdentityUi(trigger, vaultBridge, identity);
    if (previousUserId !== (identity?.userId ?? null)) {
      listeners.forEach((listener) => listener(identity));
    }
  };

  const unsubscribe = gateway.onIdentityChange((nextIdentity, event) => {
    const unexpectedSignOut = shouldNotifyUnexpectedSignOut({
      event,
      hadIdentity: identity !== null,
      hasNextIdentity: nextIdentity !== null,
      suppressed: suppressSignedOutNotice,
    });
    applyIdentity(nextIdentity);
    if (event === "PASSWORD_RECOVERY" && nextIdentity !== null) {
      recoveringPassword = true;
      showAuthPanel(dialog, "recovery");
      if (!dialog.open) dialog.showModal();
      return;
    }
    if (unexpectedSignOut) {
      recoveringPassword = false;
      showAuthPanel(dialog, "signed-out");
      setOutcome(dialog, UNEXPECTED_SIGN_OUT_MESSAGE, "error");
      if (!dialog.open) dialog.showModal();
      return;
    }
    if (!recoveringPassword && nextIdentity !== null && dialog.open) dialog.close();
  });

  try {
    identity = await gateway.getIdentity();
  } catch (error) {
    if (authReturn.detected) {
      window.history.replaceState(window.history.state, "", authReturn.cleanRelativeUrl);
    }
    unsubscribe();
    throw error;
  }
  updateIdentityUi(trigger, vaultBridge, identity);

  if (authReturn.detected) {
    window.history.replaceState(window.history.state, "", authReturn.cleanRelativeUrl);
  }
  if (authReturn.errorMessage !== null) {
    showAuthPanel(dialog, "signed-out");
    setOutcome(dialog, authReturn.errorMessage, "error");
    if (!dialog.open) dialog.showModal();
  }

  trigger.addEventListener("click", () => {
    showAuthPanel(dialog, recoveringPassword ? "recovery" : identity === null ? "signed-out" : "signed-in");
    const email = dialog.querySelector<HTMLElement>("[data-auth-email]");
    if (email !== null) email.textContent = identity?.email ?? "";
    dialog.showModal();
  });

  dialog.querySelector<HTMLButtonElement>("[data-auth-close]")?.addEventListener("click", () => {
    dialog.close();
  });

  googleButton?.addEventListener("click", async () => {
    if (!config.googleEnabled) return;
    setBusy(dialog, true);
    try {
      options.beforeExternalAuthRedirect?.();
      await gateway.signInWithGoogle(authReturnUrl(window.location));
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
      setBusy(dialog, false);
    }
  });

  dialog.querySelector<HTMLFormElement>("[data-auth-form='otp']")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const email = new FormData(form).get("email");
    if (typeof email !== "string") return;
    setBusy(dialog, true);
    try {
      options.beforeExternalAuthRedirect?.();
      await gateway.sendEmailLink(email, authReturnUrl(window.location));
      setOutcome(dialog, "Link inviato. Controlla la posta e torna qui con un solo clic.", "ok");
      form.reset();
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
    } finally {
      setBusy(dialog, false);
    }
  });

  dialog.querySelector<HTMLFormElement>("[data-auth-form='password']")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    const email = data.get("email");
    const password = data.get("password");
    const submitter = event.submitter as HTMLButtonElement | null;
    if (typeof email !== "string" || typeof password !== "string") return;
    setBusy(dialog, true);
    try {
      if (submitter?.value === "signup") {
        options.beforeExternalAuthRedirect?.();
        await gateway.signUpWithPassword(email, password, authReturnUrl(window.location));
        setOutcome(dialog, "Account creato. Se richiesto, conferma l’email per completare l’accesso.", "ok");
      } else {
        await gateway.signInWithPassword(email, password);
      }
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
    } finally {
      setBusy(dialog, false);
    }
  });

  dialog.querySelector<HTMLButtonElement>("[data-auth-reset]")?.addEventListener("click", async () => {
    const emailInput = dialog.querySelector<HTMLInputElement>(
      "[data-auth-form='password'] input[name='email']",
    );
    if (emailInput === null || !emailInput.reportValidity()) return;
    setBusy(dialog, true);
    try {
      options.beforeExternalAuthRedirect?.();
      await gateway.requestPasswordReset(emailInput.value, authReturnUrl(window.location));
      setOutcome(
        dialog,
        "Se l’indirizzo è registrato, riceverai un link per scegliere una nuova password.",
        "ok",
      );
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
    } finally {
      setBusy(dialog, false);
    }
  });

  dialog.querySelector<HTMLFormElement>("[data-auth-form='recovery']")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    const password = data.get("password");
    const confirmation = data.get("password_confirmation");
    if (typeof password !== "string" || typeof confirmation !== "string") return;
    const validationError = validateReplacementPassword(password, confirmation);
    if (validationError !== null) {
      setOutcome(dialog, validationError, "error");
      return;
    }
    setBusy(dialog, true);
    try {
      await gateway.updatePassword(password);
      recoveringPassword = false;
      showAuthPanel(dialog, "signed-in");
      setOutcome(dialog, "Password aggiornata. L’accesso a questo dispositivo è attivo.", "ok");
      form.reset();
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
    } finally {
      setBusy(dialog, false);
    }
  });

  dialog.querySelector<HTMLButtonElement>("[data-auth-recovery-cancel]")?.addEventListener("click", async () => {
    setBusy(dialog, true);
    suppressSignedOutNotice = true;
    try {
      await gateway.signOut();
      recoveringPassword = false;
      applyIdentity(null);
      showAuthPanel(dialog, "signed-out");
      setOutcome(dialog, "Recupero annullato. Puoi richiedere un nuovo link o accedere normalmente.", "ok");
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
    } finally {
      suppressSignedOutNotice = false;
      setBusy(dialog, false);
    }
  });

  dialog.querySelector<HTMLButtonElement>("[data-auth-signout]")?.addEventListener("click", async () => {
    setBusy(dialog, true);
    suppressSignedOutNotice = true;
    try {
      await gateway.signOut();
      recoveringPassword = false;
      applyIdentity(null);
      dialog.close();
    } catch (error) {
      setOutcome(dialog, friendlyAuthError(error), "error");
    } finally {
      suppressSignedOutNotice = false;
      setBusy(dialog, false);
    }
  });

  return {
    getIdentity: () => identity,
    getAccessToken: () => gateway.getAccessToken(),
    subscribe(listener) {
      listeners.add(listener);
      listener(identity);
      return () => listeners.delete(listener);
    },
    dispose() {
      unsubscribe();
      listeners.clear();
    },
  };
}
